import Styles from './styles.module.css';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getFirestore, onSnapshot } from 'firebase/firestore';
import { jobPostingYearDoc, SUPPORTED_JOB_POSTING_YEARS } from './jobPostingRef';
import { useAppSelector } from 'src/salon/app/hook';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { GoCheck } from 'react-icons/go';

// 求人票(年度ごとに学校へ送付する書類)は「年度」単位で管理する。
// 求人(3.recruit、アプリ上に公開する求人ページ)とはデータ・目的が異なるため、
// 編集画面は2.jobPostings/edit配下の専用画面に遷移する。
// 年度ごとの内容(企業情報・雇用条件・福利厚生・応募情報・ハッシュタグ)は
// recruits/{companyId}_2027/jobPostings/{year} のサブコレクションドキュメントに
// 保存されている(親は既存のフラットドキュメント {companyId}_2027 そのもの。
// パスの組み立ては ./jobPostingRef に集約)。会社概要・PR(summary)は3.recruitと
// 共有の年度非依存データのため、引き続き recruits/{companyId}_2027 本体から読む
// (求人票専用のタイトルデータを年度ごとに別途持っているわけではない)。
// 年度ごとの行は当面この配列に手動で追加していく運用とする。
type JobPostingYearRow = {
  year: string;
};

// 年度は jobPostingRef.ts の SUPPORTED_JOB_POSTING_YEARS で一元管理する。
const jobPostingYears: JobPostingYearRow[] = SUPPORTED_JOB_POSTING_YEARS.map((year) => ({ year }));

const GRADUATE_AGES = ['20', '18', '17'] as const;

// PDF(20/18/17歳卒)は募集区分(target.target)で選ばれている区分だけ閲覧させる。
// 20歳卒は「専門(20歳卒)」が必須選択のため常に対象。18/17は高専の各区分を
// 選択しているときだけ表示する(confirm/index.tsx の出し分けと同じ基準)。
const AGE_TARGET_CONDITION: Record<(typeof GRADUATE_AGES)[number], string | null> = {
  '20': null,
  '18': '高専(18歳卒)',
  '17': '高専(17歳卒)',
};

// 求人票PDFは年度に関わらず jobPostingsPdf/{companyId}_{year}_{age}.pdf に保存される
// (functions/jobPostings.js の generateJobPostingPdf / generateJobPostingPdfV2)。
const jobPostingPdfUrl = (documentLabel: string, age: string): string =>
  `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/jobPostingsPdf%2F${documentLabel}_${age}.pdf?alt=media&token=`;

const JobPostings: React.FC = () => {
  const navigate = useNavigate();
  const company = useAppSelector((state) => state.company.company);
  const db = getFirestore();

  const [title, setTitle] = useState<string>('');
  // 旧・初回設定フロー(1.manual)は recruits/{companyId}_2027 に jobPostingsStatus:1 を
  // 書いていた。新フローはサブコレクション recruits/{companyId}/jobPostings/{year} に書く。
  // どちらか一方でも確定済みなら「作成済み」とみなす(2027年度のみ旧フロー対象)。
  const [legacyCreated2027, setLegacyCreated2027] = useState(false);
  const [subCreatedByYear, setSubCreatedByYear] = useState<Record<string, boolean>>({});
  const [sendingByYear, setSendingByYear] = useState<Record<string, boolean>>({});
  // 募集区分。2027年度は旧フロー(フラット)分もフォールバックで見る。
  const [legacyTarget2027, setLegacyTarget2027] = useState<string[]>([]);
  const [subTargetByYear, setSubTargetByYear] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!company?.id) return;

    const summaryDocId = `${company.id}_2027`;
    const unsubscribe = onSnapshot(doc(db, 'recruits', summaryDocId), (docSnap) => {
      const data = docSnap.data() ?? {};
      setTitle(data?.summary?.title ?? '');
      // フラットの jobPostingsStatus は2028年度以降を確定したときにも立つため、
      // 2027年度の中身(employment20)がフラットにあるときだけ2027年度の作成済みとみなす
      // (isCompleted フラグが無い古いデータは入力済みとして扱い、未完了と明示されたものだけ除く)。
      setLegacyCreated2027(
        data?.jobPostingsStatus === 1
          && !!data?.employment20
          && data.employment20.isCompleted !== false,
      );
      setLegacyTarget2027(Array.isArray(data?.target?.target) ? data.target.target : []);

      // 送付先の選択状況は現状 recruits/{companyId}_2027 の {year}_sending
      // フィールド(現時点で実在するのは2027_sendingのみ)で判定する。
      const nextSending: Record<string, boolean> = {};
      jobPostingYears.forEach((row) => {
        const sendingList = data[`${row.year}_sending`];
        nextSending[row.year] = Array.isArray(sendingList) && sendingList.length > 0;
      });
      setSendingByYear(nextSending);
    });

    return () => unsubscribe();
  }, [company.id]);

  useEffect(() => {
    if (!company?.id) return;

    const unsubscribes = jobPostingYears.map((row) => {
      const jobPostingRef = jobPostingYearDoc(db, company.id, row.year);
      return onSnapshot(jobPostingRef, (docSnap) => {
        const created = docSnap.data()?.jobPostingsStatus === 1;
        setSubCreatedByYear((prev) => ({ ...prev, [row.year]: created }));

        const t = docSnap.data()?.target?.target;
        setSubTargetByYear((prev) => ({ ...prev, [row.year]: Array.isArray(t) ? t : [] }));
      });
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [company.id]);

  const isCreated = (year: string) =>
    !!subCreatedByYear[year] || (year === '2027' && legacyCreated2027);

  // その年度で選択されている募集区分(サブコレクション優先、2027は旧フロー分もマージ)
  const targetsForYear = (year: string): string[] => {
    const sub = subTargetByYear[year] ?? [];
    const legacy = year === '2027' ? legacyTarget2027 : [];
    return Array.from(new Set([...sub, ...legacy]));
  };

  // 指定の卒業区分(20/18/17歳卒)が、その年度の募集対象に入っているか
  const isAgeInScope = (year: string, age: (typeof GRADUATE_AGES)[number]): boolean => {
    const condition = AGE_TARGET_CONDITION[age];
    if (!condition) return true; // 20歳卒は必須
    return targetsForYear(year).includes(condition);
  };

  return (
    <div className={Styles.box}>
      <div className={Styles.recruit_table}>
        <div className={Styles.recruit_table_nav}>
          <h3 className={Styles.recruit_table_nav_year}>卒業年度</h3>
          <h3 className={Styles.recruit_table_nav_title}>求人タイトル</h3>
          <h3 className={Styles.recruit_table_nav_edit}>求人票の編集</h3>
          <h3 className={Styles.recruit_table_nav_sending}>送付先の選択</h3>
          {GRADUATE_AGES.map((age) => (
            <h3 key={age} className={Styles.recruit_table_nav_pdf}>{age}歳卒</h3>
          ))}
        </div>

        {jobPostingYears.map((row) => {
          const documentLabel = company?.id ? `${company.id}_${row.year}` : '';
          const created = isCreated(row.year);

          return (
            <div key={row.year} className={Styles.items_table}>
              <div className={Styles.items_table_box_year}>
                <h4>{row.year}年</h4>
              </div>
              <div className={Styles.items_table_box_title}>
                <h4>{title || '未設定'}</h4>
              </div>
              <div className={Styles.items_table_box_edit}>
                {created ? (
                  // 確定後(=学校への発送に使われるPDFを生成済み)は内容が
                  // ずれると困るため、編集画面には遷移させず「作成済み」表示のみにする。
                  <span className={Styles.chip_done}>
                    <GoCheck className={Styles.chip_done_icon} />作成済み
                  </span>
                ) : (
                  <button type="button" className={Styles.rowButton} onClick={() => navigate(`/service/jobPostings/${row.year}`)}>編集する</button>
                )}
              </div>
              <div className={Styles.items_table_box_sending}>
                {company.contract ? (
                  // 有料事業者は学校を選ばず全校へ送付する仕様のため、選択操作自体が
                  // 存在しない。編集ボタンと同様、ボタンごと「全校選択済み」表示にする。
                  <span className={Styles.chip_done}>
                    <GoCheck className={Styles.chip_done_icon} />全校選択済み
                  </span>
                ) : sendingByYear[row.year] ? (
                  // 送付先を1校以上選択済みなら、ボタンは出さず「選択済み」表示のみにする。
                  <span className={Styles.chip_done}>
                    <GoCheck className={Styles.chip_done_icon} />選択済み
                  </span>
                ) : (
                  <button type="button" className={Styles.rowButton} onClick={() => navigate(`/service/jobPostings/${row.year}/sending`)}>送付先の選択</button>
                )}
              </div>
              {GRADUATE_AGES.map((age) => (
                <div key={age} className={Styles.items_table_box_pdf}>
                  {!isAgeInScope(row.year, age) ? (
                    // 募集区分に入っていない卒業区分は PDF を見せない(発送対象外)。
                    <span className={Styles.pdf_out_of_scope} aria-label={`${age}歳卒は募集区分外です`}>—</span>
                  ) : created ? (
                    <a
                      href={jobPostingPdfUrl(documentLabel, age)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IoDocumentTextOutline className={Styles.pdf_icon} />
                    </a>
                  ) : (
                    // 求人票を確定するまで PDF は生成されないため、リンクにせず淡色で無効表示にする。
                    <IoDocumentTextOutline className={`${Styles.pdf_icon} ${Styles.pdf_icon_disabled}`} aria-label="求人票の作成後に表示されます" />
                  )}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default JobPostings;
